import { createContext, useContext, useMemo } from "react";

// --- Types ---

export type UIThemeType = "success" | "error" | "warning" | "info" | "danger";

export interface AlertState {
  isOpen: boolean;
  title: string;
  message: string;
  type: "success" | "error" | "warning" | "info";
  onClose?: () => void;
}

export interface ConfirmState {
  isOpen: boolean;
  title: string;
  message: string;
  type: "danger" | "warning" | "info";
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onClose?: () => void;
}

export interface ToastState {
  isVisible: boolean;
  title?: string;
  message: string;
  type: "success" | "error" | "warning" | "info";
  duration?: number;
}

export interface UIState {
  alert: AlertState;
  confirm: ConfirmState;
  toast: ToastState;
}

export interface UIDispatch {
  showAlert: (config: Omit<AlertState, "isOpen">) => void;
  hideAlert: () => void;
  showConfirm: (config: Omit<ConfirmState, "isOpen">) => void;
  hideConfirm: () => void;
  showToast: (config: Omit<ToastState, "isVisible">) => void;
  hideToast: () => void;
}

// --- Context Objects ---

export const UIStateContext = createContext<UIState | undefined>(undefined);
export const UIDispatchContext = createContext<UIDispatch | undefined>(
  undefined,
);

// --- Hooks ---

/**
 * 1. UI State Hook: Basic provision of alert/confirm/toast states.
 */
export function useUIState() {
  const context = useContext(UIStateContext);
  if (context === undefined) {
    throw new Error("useUIState must be used within a UIProvider");
  }
  return context;
}

/**
 * 2. UI Dispatch Hook: Access to showAlert/showConfirm/showToast actions.
 */
export function useUIDispatch() {
  const context = useContext(UIDispatchContext);
  if (context === undefined) {
    throw new Error("useUIDispatch must be used within a UIProvider");
  }
  return context;
}

/**
 * 3. Sovereign UI Singleton Hook: High-level access to globalAlert, globalConfirm, globalToast.
 */
export function useUI() {
  const { showAlert, showConfirm, showToast } = useUIDispatch();

  return useMemo(
    () => ({
      globalAlert: (
        arg1: string | Omit<AlertState, "isOpen">,
        message?: string,
        type: "success" | "error" | "warning" | "info" = "info",
      ) => {
        if (typeof arg1 === "object") {
          showAlert(arg1 as AlertState);
        } else {
          showAlert({ title: arg1, message: message || "", type });
        }
      },
      globalConfirm: (
        arg1: string | Omit<ConfirmState, "isOpen">,
        message?: string,
        onConfirm?: () => void,
        type: "danger" | "warning" | "info" = "danger",
        confirmText?: string,
        cancelText?: string,
      ) => {
        if (typeof arg1 === "object") {
          showConfirm(arg1 as ConfirmState);
        } else {
          showConfirm({
            title: arg1,
            message: message || "",
            onConfirm: onConfirm || (() => {}),
            type,
            confirmText,
            cancelText,
          });
        }
      },
      globalToast: (
        arg1: string | Omit<ToastState, "isVisible">,
        message?: string,
        type: "success" | "error" | "warning" | "info" = "info",
        duration?: number,
      ) => {
        if (typeof arg1 === "object") {
          showToast(arg1 as ToastState);
        } else {
          showToast({ title: arg1, message: message || "", type, duration });
        }
      },
    }),
    [showAlert, showConfirm, showToast],
  );
}
